import posthog, { CaptureOptions, Properties } from 'posthog-js';

import { isProductionEnv } from '~utils/isProductionEnv';

// posthog-js logs a console error per call when it was never initialized
// (e.g. TREAD__POSTHOG_PROJECT_TOKEN unset, or an automated browser session),
// so every method no-ops until init has run.
const isEnabled = () => posthog.__loaded;

// The single seam for product analytics: call sites use this controller, never
// posthog directly, so instrumentation stays consistent and swappable.
// $groupidentify is a group-property UPSERT on the shared company group record, which —
// unlike events — carries no environment discriminator. A staging session would overwrite
// production company data irreversibly, so grouping only runs in production (the BE's
// posthog-ruby client is gated the same way). Events stay ungated: they are tagged with
// the `environment` super property and filterable.
const isGroupIdentifyAllowed = isProductionEnv;

export const AnalyticsController = {
  // Associate subsequent events with the company the user is acting in
  group: (companyId: string, properties?: Properties) => {
    if (isEnabled() && isGroupIdentifyAllowed()) {
      posthog.group('company', companyId, properties);
    }
  },
  // Tie the anonymous session to the logged-in user
  identify: (userId: string, properties?: Properties) => {
    if (isEnabled()) {
      posthog.identify(userId, properties);
    }
  },
  // Attach persistent super-properties to every subsequent captured event
  register: (properties: Properties) => {
    if (isEnabled()) {
      posthog.register(properties);
    }
  },
  registerForSession: (properties: Properties) => {
    if (isEnabled()) {
      posthog.register_for_session(properties);
    }
  },
  // Clear identity on logout so the next session starts anonymous
  reset: () => {
    if (isEnabled()) {
      posthog.reset();
    }
  },
  // Generic track method for custom events
  track: (eventName: string, properties?: Properties, options?: CaptureOptions) => {
    if (isEnabled()) {
      posthog.capture(eventName, properties, options);
    }
  },
  unregisterForSession: (property: string) => {
    if (isEnabled()) {
      posthog.unregister_for_session(property);
    }
  },
};
